/**
 * Screen-local fetch module for the partner-credentials screen (epic 006
 * decision #13 / A2 b): `lib/api.ts` is never edited, so this screen owns its
 * endpoints. The envelope/error handling below mirrors `lib/api.ts`'s private
 * `request()` line for line — copied, not imported (`request` is not exported).
 * Wire types come from `@leads-platform/shared`; there is no local type mirror.
 */

import type {
  CreatePartnerCredential,
  CredentialKeyStatusResponse,
  PartnerCredentialResponse,
  PartnerCredentialRowResponse,
  ReencryptResponse,
  RotatePartnerCredential,
} from '@leads-platform/shared';
import { apiFetch } from '../../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * Error twin of `lib/api.ts`'s `ApiError` — same `{ status, message,
 * validationErrors }` shape, so screens catch this module's errors exactly as
 * they catch the shared client's.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Map a backend validation error array (`{path, message, code}`) into a
 * `Record<path, message[]>` for per-field surfacing. The root path is
 * normalized to `_form`. Verbatim from `lib/api.ts`.
 */
function mapValidationErrors(errors: unknown): Record<string, string[]> | undefined {
  if (!Array.isArray(errors)) return undefined;
  const out: Record<string, string[]> = {};
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { path?: string; message?: string };
    const key = e.path && e.path.length > 0 ? e.path : '_form';
    const msg = e.message ?? 'Invalid';
    if (!out[key]) out[key] = [];
    out[key].push(msg);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** API response envelope — matches the backend's ApiResponse<T> shape. */
interface ApiResponse<T> {
  data: T;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(`${API_BASE_URL}${ADMIN_PREFIX}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      typeof errorData.message === 'string' && errorData.message.length > 0
        ? errorData.message
        : response.statusText;
    throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
  }

  const text = await response.text();
  if (text.length === 0) {
    throw new ApiError(0, 'Empty response from the API');
  }
  const envelope: ApiResponse<T> = JSON.parse(text);
  return envelope.data;
}

/**
 * The create body the SPA puts on the wire: `advertiserId` arrives from a
 * `<select>` as a string, and the shared `createPartnerCredentialSchema`
 * declares it `zCoerceBigInt()` — wire input `string | number | bigint`,
 * coerced output `bigint`. Shared exports only the coerced output type
 * (`CreatePartnerCredential`), so the wire side is derived from it and every
 * other field stays in lockstep with the schema (conventions §Type-safety).
 */
export type CreatePartnerCredentialWire = Omit<CreatePartnerCredential, 'advertiserId'> & {
  advertiserId: string;
};

/** Every stored credential, optionally narrowed to one advertiser. */
export function getPartnerCredentials(
  advertiserId?: string
): Promise<PartnerCredentialRowResponse[]> {
  const query = advertiserId ? `?advertiserId=${encodeURIComponent(advertiserId)}` : '';
  return request<PartnerCredentialRowResponse[]>(`/partner-credentials${query}`);
}

/** Encryption-key custody read (ADR-011 #5/#7) — drives the screen banner. */
export function getPartnerCredentialKeyStatus(): Promise<CredentialKeyStatusResponse> {
  return request<CredentialKeyStatusResponse>('/partner-credentials/key-status');
}

export function createPartnerCredential(
  body: CreatePartnerCredentialWire
): Promise<PartnerCredentialResponse> {
  return request<PartnerCredentialResponse>('/partner-credentials', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function rotatePartnerCredential(
  id: string,
  body: RotatePartnerCredential
): Promise<PartnerCredentialResponse> {
  return request<PartnerCredentialResponse>(`/partner-credentials/${id}/rotate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function revokePartnerCredential(id: string): Promise<PartnerCredentialResponse> {
  return request<PartnerCredentialResponse>(`/partner-credentials/${id}/revoke`, {
    method: 'POST',
  });
}

/** ADR-011 #5 — the one rotation command behind the screen's Re-encrypt button. */
export function reencryptPartnerCredentials(): Promise<ReencryptResponse> {
  return request<ReencryptResponse>('/partner-credentials/re-encrypt', { method: 'POST' });
}
